import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setUser } from '@/store/reducers';
import { firebaseUtils } from '@/utils';
import { IUser } from '@/models';
import { isUserAnonymous } from '@/utils';

/**
 * Hook that listens to Firebase auth state changes for cross-tab sync.
 * When user logs in/out in one tab, all other tabs will update automatically.
 * Anonymous users are also synced to maintain consistent uid across tabs.
 */
export const useAuthStateListener = (appReady: boolean) => {
  const dispatch = useDispatch();
  // undefined = not yet processed, string = last processed user uid
  const lastUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!appReady) return;

    const unsubscribe = firebaseUtils.onAuthStateChanged(async fbUser => {
      if (!fbUser) {
        return;
      }

      // Skip if user hasn't actually changed (prevents duplicate processing)
      if (fbUser.uid === lastUserIdRef.current) {
        return;
      }
      lastUserIdRef.current = fbUser.uid;

      // For anonymous users - set user directly without premium API call
      if (isUserAnonymous(fbUser)) {
        const anonymousUser: IUser = {
          uid: fbUser.uid,
          phoneNumber: '',
          email: '',
          displayName: '',
          photoURL: '',
          isAnonymous: true,
          isPremium: false,
        };
        dispatch(setUser(anonymousUser));
        return;
      }

      // For logged-in users - fetch premium details
      const newUser = await firebaseUtils.fbUserToUser(fbUser);
      dispatch(setUser(newUser));
    });

    return () => unsubscribe();
  }, [appReady, dispatch]);
};
